import React, { useCallback, useEffect, useState } from 'react';
import CmsAPI from '../../../common/apis/CmsAPI';
import { timeSince, timeStringToSeconds } from '../../utils/TimeUtil';
import NewComment from './NewComment';
import ReplyComment from './ReplyComment';
import Comment from './Comment';
import styles from './comments.module.scss';
import LinkedContainer from '../LinkedContainer/LinkedContainer';
import { useTranslation } from 'react-i18next';
import { ESCAPE_KEY_CODE } from '../../constants/common';
import { useInPlayerCommentContext } from '../../context/InPlayerCommentContext';

const timestampRegex = /\b\d{1,2}:\d{2}(?::\d{2})?\b/g;
const positionRegex = /\[(\d{1,2}|100),\s*(\d{1,2}|100)\] /g;
const resolveCommentMarkRegex = /\[✅\] /g;

const Comments = ({
  showNewCommentForm,
  chapters,
  callActions,
  storyId,
  user,
  author,
  setShowNewCommentForm,
  currentUserName,
  setCurrentUserName,
  isSharedPage = false,
  ignoreTimestampsBefore = 0,
  disableInPlayerComments = false,
}) => {
  const commentInit = {
    authorName: '',
    content: '',
    parent: null,
    username: user ? user.username : null,
  };

  const [isLoading, setIsLoading] = useState(false);
  const [rootComments, setRootComments] = useState(null);
  const [commentReplies, setCommentReplies] = useState(null);
  const [loadComments, setLoadComments] = useState(false);
  const [newComment, setNewComment] = useState(commentInit);
  const [addReplyTo, setAddReplyTo] = useState(null);
  const [commentToEdit, setCommentToEdit] = useState(null);
  const [isReply, setIsReply] = useState(null);
  const [isNewReactions, setIsNewReactions] = useState(false);
  const [replyProcessing, setReplyProcessing] = useState(false);

  const { newPositionComment, onSaved, updateTimestampComments } = useInPlayerCommentContext();

  const { t } = useTranslation();

  useEffect(() => {
    if (storyId) {
      getComments();
    }
  }, [storyId]);

  useEffect(() => {
    if (loadComments === true) {
      getComments();
    }
  }, [loadComments]);

  const updateCommentsWithTimestamp = useCallback((comments) => {
    const commentsWithTimestamp = {};

    comments.forEach(comment => {
      const stringTimestamps = comment?.content?.match(timestampRegex) || [];

      const secondsTimestamps = stringTimestamps.map(timestamp => timeStringToSeconds(timestamp));

      const cleanedText = comment?.content?.replace(timestampRegex, "").trim();

      secondsTimestamps.forEach(secondsValue => {

        const preparedComment = { id: comment?.id, text: cleanedText, authorName: comment.authorName, authorAvatar: comment?.avatar, position: comment?.position, resolved: comment?.resolved, }
        if (commentsWithTimestamp[secondsValue]) {
          commentsWithTimestamp[secondsValue].push(structuredClone(preparedComment));
          return;
        }

        commentsWithTimestamp[secondsValue] = [structuredClone(preparedComment)];
      })
    });

    updateTimestampComments(commentsWithTimestamp);
  }, [updateTimestampComments]);

  const prepareComment = (comment) => {
    let preparedComment = {};
    const resolvedMarkMatch = resolveCommentMarkRegex.exec(comment.content);
    if (resolvedMarkMatch) {
      if (resolvedMarkMatch.index === 0) {
        preparedComment.resolved = true;
      }
    }
    const positionMatch = comment.content.match(positionRegex);
    if (positionMatch?.length) {
      const parsedPosition = JSON.parse(positionMatch[0]);
      preparedComment.position = {
        x: parsedPosition[0],
        y: parsedPosition[1]
      }
    }
    preparedComment.id = comment.id;
    preparedComment.authorName = comment.authorName;
    if (comment.parent !== null) {
      preparedComment.parent = comment.parent;
    }
    preparedComment.username = comment.username;
    preparedComment.content = comment.content.replace(resolveCommentMarkRegex, '').replace(positionRegex, '');
    preparedComment.since = timeSince(comment.date);
    const commentDate = new Date(comment.date);
    // Get time in seconds
    preparedComment.date = commentDate.getTime() / 1000;
    if (comment.hasOwnProperty('author')) {
      preparedComment.avatar = comment.author.avatar;
    }
    preparedComment.reactions = comment.reactions;
    return preparedComment;
  }

  const getComments = useCallback(async () => {
    setIsLoading(true);

    try {
      const commentsRes = await CmsAPI.getComments(storyId);
      let commentsArr = [];
      let repliesArr = [];
      commentsRes?.result.forEach((comment) => {
        const preparedComment = prepareComment(comment);
        if (disableInPlayerComments && preparedComment.hasOwnProperty('position')) {
          return;
        }
        if (comment.parent === null) {
          commentsArr.push(preparedComment);
        } else {
          repliesArr.push(preparedComment);
        }
      });
      console.log({ commentsArr, repliesArr });
      updateCommentsWithTimestamp([...commentsArr, ...repliesArr]?.filter(comment => {
        const commentDate = new Date(comment.date);
        return commentDate.getTime() / 1000 > ignoreTimestampsBefore
      }) || [])
      setRootComments(commentsArr);
      setCommentReplies(repliesArr);
      setIsLoading(false);
      setLoadComments(false);

    } catch (err) {
      setIsLoading(false);
      console.log('Error getting comments', err);
    }
  }, [updateCommentsWithTimestamp, disableInPlayerComments]);

  useEffect(() => {
    rootComments && commentReplies && updateCommentsWithTimestamp([...rootComments, ...commentReplies].filter(comment => comment.date > ignoreTimestampsBefore));
  }, [ignoreTimestampsBefore])

  const handleDeleteClick = () => {
    setShowNewCommentForm({ show: false, timestamp: false });
    setNewComment(commentInit);
    setAddReplyTo(null);
    setCommentToEdit(null);
    setIsReply(false);
  };

  const handleDeleteComment = async id => {
    try {
      const deleteCommentRes = await CmsAPI.deleteComment(storyId, id);
      if (deleteCommentRes.result === true) {
        setLoadComments(true);
      }
    } catch (err) {
      console.log('Error deleting comment');
    }
  };

  const prepareCommentForUpdate = (comment) => {
    const preparedComment = { ...comment };
    if (preparedComment.hasOwnProperty('position')) {
      const firstTimestampMatch = timestampRegex.exec(preparedComment.content);
      if (firstTimestampMatch) {
        preparedComment.content = preparedComment.content.slice(0, firstTimestampMatch.index + firstTimestampMatch[0].length) + ` [${preparedComment.position.x},${preparedComment.position.y}]` + preparedComment.content.slice(firstTimestampMatch.index + firstTimestampMatch[0].length);
      }
    }
    if (comment.resolved) {
      if (!resolveCommentMarkRegex.exec(preparedComment.content)) {
        preparedComment.content = `[✅] ${preparedComment.content}`;
      }
    } else {
      preparedComment.content = preparedComment.content.replace('[✅] ', '');
    }
    return preparedComment;
  }

  const handleResolvedClick = useCallback(async (id) => {
    // For now resolved comment just has an check mark emoji in the beginning of the comment text
    const comment = rootComments.find(comment => comment.id === id);
    comment.resolved = !comment.resolved;
    const preparedComment = prepareCommentForUpdate(comment);
    try {
      await CmsAPI.updateComment(storyId, preparedComment);
      setLoadComments(true);
    } catch (err) {
      console.log('Error updating comment', err);
    }
  }, [rootComments]);

  const handleCancel = () => {
    setShowNewCommentForm({ show: false, timestamp: false });
    setNewComment(commentInit);
    setAddReplyTo(null);
    setCommentToEdit(null);
    setIsReply(false);
    if (isNewReactions) {
      getComments();
      setIsNewReactions(false);
    }
  };

  const onSubmitComment = async commentVal => {
    // Cache commenter name
    localStorage.setItem('commenterName', commentVal.authorName);
    const newCommentContent = commentVal?.parent ? commentVal.content.replace(`@${commentVal.parentAuthor}`, '') : commentVal.content;
    if (newCommentContent.trim().length > 0) {
      setReplyProcessing(true);
      try {
        if (commentToEdit !== null) {
          const preparedComment = prepareCommentForUpdate(commentVal);
          await CmsAPI.updateComment(storyId, preparedComment);
        } else {
          await CmsAPI.insertComment(storyId, commentVal);
        }
        setShowNewCommentForm({ show: false, timestamp: false });
        setLoadComments(true);
        setAddReplyTo(null);
        setCommentToEdit(null);
        setIsReply(false);
        setNewComment(commentInit);
      } catch (e) {
        console.error('Failed to reply with error:', e);
      } finally {
        setReplyProcessing(false);
      }
    } else {
      setShowNewCommentForm({ show: false, timestamp: false });
      setAddReplyTo(null);
      setCommentToEdit(null);
      setNewComment(commentInit);
    }
  };

  const onSubmitInPlayerComment = useCallback(async comment => {
    const { result } = await CmsAPI.insertComment(storyId, comment);
    if (result.username === user.username) {
      result.author = { avatar: user.avatarUri };
    }
    const preparedComment = prepareComment(result);
    setRootComments(prevState => [preparedComment, ...prevState]);
    updateCommentsWithTimestamp([preparedComment, ...rootComments, ...commentReplies]);
  }, [rootComments, commentReplies, user])

  const onCommentReplyClick = (comment, isReply) => {
    setNewComment(commentInit);
    setCommentToEdit(null);
    setAddReplyTo({ comment: comment, isReply: isReply });
  };

  const onCommentEditClick = comment => {
    setNewComment(comment);
    setCommentToEdit(comment.id);
    if (comment.parent === undefined) {
      setAddReplyTo(null);
      setIsReply(false);
    } else {
      setIsReply(true);
    }
  };

  const handleKeyPress = e => {
    if (e.keyCode === ESCAPE_KEY_CODE) {
      handleCancel();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);

    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    if (newPositionComment !== null) {
      onSubmitInPlayerComment(newPositionComment);
      onSaved();
    }
  }, [newPositionComment])

  return (
    <div className={styles.commentsWrapper}>
      <NewComment
        user={user}
        currentUserName={currentUserName}
        comment={newComment}
        setCurrentUserName={setCurrentUserName}
        handleCancel={handleCancel}
        onSubmitComment={onSubmitComment}
        inReplyTo={null}
        isEdit={commentToEdit !== null}
        replyProcessing={replyProcessing}
        showNewCommentForm={showNewCommentForm}
        setShowNewCommentForm={setShowNewCommentForm}
      />
      <LinkedContainer
        isSharedPage={isSharedPage}
        storyId={storyId}
        items={chapters}
        title={t('my_video.general.chapter')}
        chapter={true}
      />
      <LinkedContainer
        isSharedPage={isSharedPage}
        storyId={storyId}
        items={(callActions ?? []).filter(item => item.url !== null)}
        title={t(`${(callActions ?? []).length > 1 ? 'my_video.general.cta_plural' : 'my_video.general.cta'}`)}
        cta={true}
      />

      <div>
        {rootComments &&
          rootComments.map((c, index) => {
            const cReplies = commentReplies && commentReplies.filter(item => item.parent === c.id);
            return (
              <div key={`${author}-${c.id}`} className={index == 0 ? styles.commentWrapperTop : styles.commentWrapper}>
                {/* adding a reply */}

                {!(commentToEdit && commentToEdit === c.id) ? (
                  <Comment
                    key={c.id}
                    comment={c}
                    user={user}
                    username={currentUserName}
                    setUsername={setCurrentUserName}
                    setAddReplyTo={setAddReplyTo}
                    handleDeleteComment={handleDeleteComment}
                    handleResolvedClick={handleResolvedClick}
                    storyAuthor={author}
                    storyId={storyId}
                    onCommentReplyClick={onCommentReplyClick}
                    onCommentEditClick={onCommentEditClick}
                    handleDeleteClick={handleDeleteClick}
                    setIsNewReactions={setIsNewReactions}
                    ignoreTimestampsBefore={ignoreTimestampsBefore}
                  />
                ) : (
                  <ReplyComment
                    user={user}
                    currentUserName={currentUserName}
                    setCurrentUserName={setCurrentUserName}
                    comment={newComment}
                    handleCancel={handleCancel}
                    onSubmitComment={onSubmitComment}
                    inReplyTo={addReplyTo?.comment.id}
                    isEdit={commentToEdit !== null}
                    isReply={isReply}
                    replyProcessing={replyProcessing}
                  />
                )}

                {/* New Comment for Replys */}
                {addReplyTo && addReplyTo?.comment.id === c.id ? (
                  <ReplyComment
                    user={user}
                    currentUserName={currentUserName}
                    setCurrentUserName={setCurrentUserName}
                    comment={newComment}
                    parentAuthor={c.authorName}
                    handleCancel={handleCancel}
                    onSubmitComment={onSubmitComment}
                    inReplyTo={addReplyTo?.comment.id}
                    isEdit={commentToEdit !== null}
                    isReply={isReply}
                    replyProcessing={replyProcessing}
                  />
                ) : null}

                {/* New Comment for nested Replys */}
                {addReplyTo && addReplyTo?.comment.parent === c.id ? (
                  <ReplyComment
                    user={user}
                    currentUserName={currentUserName}
                    setCurrentUserName={setCurrentUserName}
                    comment={newComment}
                    parentAuthor={addReplyTo?.comment.authorName}
                    handleCancel={handleCancel}
                    onSubmitComment={onSubmitComment}
                    inReplyTo={addReplyTo?.comment.parent}
                    isEdit={commentToEdit !== null}
                    isReply={isReply}
                    replyProcessing={replyProcessing}
                  />
                ) : null}

                {/* render replies */}
                {cReplies ?
                  cReplies.map(r => {
                    return (
                      <div key={`${user}-${r.id}`}>
                        {commentToEdit && commentToEdit === r.id ? (
                          <ReplyComment
                            user={user}
                            currentUserName={currentUserName}
                            setCurrentUserName={setCurrentUserName}
                            comment={newComment}
                            handleCancel={handleCancel}
                            onSubmitComment={onSubmitComment}
                            inReplyTo={addReplyTo}
                            isEdit={commentToEdit !== null}
                            isReply={isReply}
                            replyProcessing={replyProcessing}
                          />
                        ) : (
                          <Comment
                            key={r.id || `comment-${index}`}
                            comment={r}
                            storyId={storyId}
                            username={currentUserName}
                            setUsername={setCurrentUserName}
                            replies={[]}
                            user={user}
                            setAddReplyTo={setAddReplyTo}
                            handleDeleteComment={handleDeleteComment}
                            handleResolvedClick={handleResolvedClick}
                            isReply={true}
                            storyAuthor={author}
                            onCommentReplyClick={onCommentReplyClick}
                            onCommentEditClick={onCommentEditClick}
                            handleDeleteClick={handleDeleteClick}
                            setIsNewReactions={setIsNewReactions}
                          />
                        )}
                      </div>
                    );
                  }) : null}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default Comments;
